import React, { useState, useEffect, useCallback } from 'react';
import styles from './BankingHeader_modal.module.css';
import { useNavigate } from 'react-router-dom';
import { utils, write } from 'xlsx';
import { saveAs } from 'file-saver';
import Modal from 'react-modal';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

// 하위 모달 컴포넌트
import ManagementModal from "./managementComponent/ManagementModal";
import DBInputModal from "../components/modal/DBInputModal";
import ManagerDBinputModal from "../components/modal/ManagerDBinputModal";

// 날짜 포맷 함수 (외부 분리하여 재선언 방지)
const formatDateTime = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
           `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

function BankingHeaderModal({ onClose }) {
    const navigate = useNavigate();
    const { t } = useTranslation();

    // 상태 관리
    const [userName, setUserName] = useState('');
    const [authLevel, setAuthLevel] = useState(null);
    const [logModalOpen, setLogModalOpen] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // 하위 모달 상태들
    const [isManagerDBModalOpen, setIsManagerDBModalOpen] = useState(false);
    const [isManagementModalOpen, setIsManagementModalOpen] = useState(false);
    const [isDBInputModalOpen, setIsDBInputModalOpen] = useState(false);

    // 1. 사용자 정보 및 권한 Fetch (useEffect로 무한 루프 방지)
    useEffect(() => {
        fetch('/api/getAuthLevel', { method: 'POST' })
            .then(res => res.ok ? res.json() : Promise.reject(res))
            .then(data => {
                if (data) {
                    setUserName(data.userName);
                    setAuthLevel(data.authLevel);
                }
            })
            .catch(error => console.error("Auth Fetch Error:", error));
    }, []);

    // 2. 로그 다운로드 로직 (기존 형식 유지)
    const handleDownload = useCallback((dataToDownload) => {
        const formattedData = dataToDownload.map(row => ({
            ...row,
            accessTime: formatDateTime(row.accessTime)
        }));

        const ws = utils.json_to_sheet(formattedData);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, "LogData");

        const wbout = write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([wbout], { type: "application/octet-stream" }), 'logData.xlsx');
    }, []);

    const handleLogDownload = async () => {
        const reqOption = {
            method: 'post',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ startDate, endDate })
        };
        
        try {
            const response = await fetch('/api/getAllLogData', reqOption);
            const data = await response.json();
            if (data?.data) handleDownload(data.data);
            if (data?.message) alert(data.message);
        } catch (err) {
            console.error("Log Download Error:", err);
        }
    };

    // 공개코드 매핑 엑셀 다운로드 (2026-11-05) — 로그인 전원
    //   서버가 매일 04:00 갱신하는 최신 전체본(공개 + 미공개)을 받는다. 익명화 열쇠라 사내 전용
    const handleCodeMapDownload = async () => {
        const ok = await Swal.fire({
            icon: 'warning',
            title: t('공개코드 매핑 다운로드'),
            html: t('내부 코드와 외부 공개 코드의 대응표입니다.') + '<br/>' +
                  t('사내 전용이며 외부(협력사·외부 메일 등)로 전달하면 안 됩니다.') + '<br/>' +
                  t('다운로드 기록이 남습니다.'),
            showCancelButton: true,
            confirmButtonText: t('다운로드'),
            cancelButtonText: t('닫기'),
        });
        if (!ok.isConfirmed) return;
        try {
            const res = await fetch('/api/downloadCodeMap', { method: 'POST' });
            if (!res.ok) {
                const msg = await res.json().then(d => d?.message).catch(() => null);
                Swal.fire({ icon: 'error', text: msg || t('파일을 받지 못했습니다.') });
                return;
            }
            const date = res.headers.get('X-Code-Map-Date') || '';
            const blob = await res.blob();
            saveAs(blob, `공개코드_매핑_${date}.xlsx`);
        } catch (err) {
            console.error("Code Map Download Error:", err);
            Swal.fire({ icon: 'error', text: t('파일을 받지 못했습니다.') });
        }
    };

    // 3. 로그아웃 (기존 로직 유지)
    const handleLogout = () => {
        fetch('/api/logout', { method: 'POST' })
            .then(res => res.ok ? res.json() : Promise.reject(res))
            .then(data => {
                if (data === true) {
                    navigate('/');
                    window.location.reload();
                }
            })
            .catch(err => console.error("Logout Error:", err));
    };

    return (
        <div className={styles.modalBackground} onClick={onClose}>
            <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                
                {/* 관리자(박준수) 전용 메뉴 */}
                {userName === '박준수' && (
                    <>
                        <p onClick={() => setIsManagerDBModalOpen(true)} className={styles.userMenu}>
                            {t('관리자 db 업데이트')}
                        </p>
                        {isManagerDBModalOpen && (
                            <ManagerDBinputModal
                                isOpen={isManagerDBModalOpen}
                                onClose={() => setIsManagerDBModalOpen(false)}
                                onUpload={(file) => console.log("업로드된 파일:", file)}
                            />
                        )}
                    </>
                )}

                {/* 권한 레벨 3 이상 공통 메뉴 */}
                {authLevel !== null && authLevel >= 3 && (
                    <>
                        <p onClick={() => setIsDBInputModalOpen(true)} className={styles.userMenu}>
                            {t('DB Input')}
                        </p>
                        {isDBInputModalOpen && <DBInputModal onClose={() => setIsDBInputModalOpen(false)} />}
                        
                        <p onClick={() => setIsManagementModalOpen(true)} className={styles.userMenu}>
                            {t('관리 페이지')}
                        </p>
                        {isManagementModalOpen && <ManagementModal onClose={() => setIsManagementModalOpen(false)} />}
                        
                        <p onClick={() => setLogModalOpen(true)} className={styles.userMenu}>
                            {t('로그 파일 다운로드')}
                        </p>
                    </>
                )}

                {/* 로그 파일 다운로드 모달 (react-modal) */}
                <Modal
                    isOpen={logModalOpen}
                    onRequestClose={() => setLogModalOpen(false)}
                    className={styles.logModalContent}
                    overlayClassName={styles.logModalOverlay}
                    ariaHideApp={false} // 접근성 에러 방지용
                >
                    <h2 className={styles.logModalHeader}>{t('로그 파일 다운로드')}</h2>
                    <label className={styles.logModalLabel}>
                        {t('시작 날짜')}:
                        <input
                            type="date"
                            value={startDate}
                            onChange={e => setStartDate(e.target.value)}
                            className={styles.logModalInput}
                        />
                    </label>
                    <label className={styles.logModalLabel}>
                        {t('종료 날짜')}:
                        <input
                            type="date"
                            value={endDate}
                            onChange={e => setEndDate(e.target.value)}
                            className={styles.logModalInput}
                        />
                    </label>
                    <button onClick={handleLogDownload} className={styles.logModalButton}>{t('다운로드')}</button>
                    <button onClick={() => setLogModalOpen(false)} className={styles.logModalButton}>{t('닫기')}</button>
                </Modal>

                {/* 공개코드 매핑 다운로드 — 로그인 전원 (서버: /api/downloadCodeMap) */}
                {authLevel !== null && authLevel >= 1 && (
                    <p onClick={handleCodeMapDownload} className={styles.userMenu}>
                        {t('공개코드 매핑 다운로드')}
                    </p>
                )}

                <p onClick={handleLogout} className={styles.userMenu}>{t('로그아웃')}</p>
            </div>
        </div>
    );
}

export default BankingHeaderModal;